import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { PopularCategoryIcon } from '../PopularCategoryIcons';
import { getEventCategoryVisual } from '../../../config/eventCategoryVisuals';

function markerUid() {
  return `evm${Math.random().toString(36).slice(2, 11)}`;
}

/** Deterministic idle-pulse timing so markers do not sync visually. */
export function markerPulseVarsFromSeed(seed) {
  const s = String(seed ?? '0');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const u = h >>> 0;
  const delaySec = ((u % 2600) / 1000).toFixed(3);
  const durSec = (4.35 + ((u >>> 10) % 1700) / 1000).toFixed(3);
  return { delaySec, durSec };
}

/**
 * Leaflet divIcon: “floating beacon” — orb + stem + ground shadow (not a map pin).
 * Unique SVG ids per instance so many markers do not clash on one map.
 */
export function buildEventCategoryMarkerIcon(
  L,
  eventType = 'other',
  isFree = false,
  isSelected = false,
  className = 'hb-leaflet-marker-icon',
  pulseSeed = null
) {
  const category = getEventCategoryVisual(eventType);
  const fillColor = category.color;
  const uid = markerUid();

  const W = isSelected ? 70 : 58;
  const H = isSelected ? 86 : 72;
  const cx = W / 2;
  const R = isSelected ? 28 : 23;
  const cyOrb = isSelected ? 36 : 30;
  const innerR = isSelected ? 20.5 : 17;
  const iconSize = isSelected ? 34 : 28;
  const iconX = cx - iconSize / 2;
  const iconY = cyOrb - iconSize / 2;

  const stemTop = cyOrb + R - 1;
  const stemH = isSelected ? 13 : 11;
  const stemW = isSelected ? 5.4 : 4.4;
  const coneTop = stemTop + stemH - 1;
  const coneBottom = H - 8;
  const coneHalfW = isSelected ? 9.5 : 7.8;
  const shadowCy = H - 5;
  const shadowRx = isSelected ? 17 : 14;
  const shadowRy = isSelected ? 5.5 : 4.5;

  const selectedRing = isSelected ? 'rgba(254, 243, 199, 0.74)' : 'rgba(255, 255, 255, 0.62)';
  const selectedRingW = isSelected ? 2.8 : 2.1;
  const outerGlowR = R + (isSelected ? 8 : 5);

  const iconMarkup = renderToStaticMarkup(
    <PopularCategoryIcon variant={category.key} className="hb-event-marker-category-svg" />
  );

  const freeBead =
    isFree ?
      `
      <g aria-hidden="true">
        <circle cx="${cx + R * 0.52}" cy="${cyOrb - R * 0.5}" r="6.2" fill="rgba(255,255,255,0.95)" />
        <circle cx="${cx + R * 0.52}" cy="${cyOrb - R * 0.5}" r="4.4" fill="#10b981" stroke="#d1fae5" stroke-width="1.2" />
        <circle cx="${cx + R * 0.52}" cy="${cyOrb - R * 0.5}" r="1.6" fill="#ffffff" fill-opacity="0.55" />
      </g>`
    : '';

  const selectedExtras = isSelected ?
    `
      <circle cx="${cx}" cy="${cyOrb}" r="${outerGlowR}" fill="none" stroke="${fillColor}" stroke-width="2" opacity="0.35" filter="url(#${uid}-glow)"/>
      <circle cx="${cx}" cy="${cyOrb}" r="${R + 3}" fill="none" stroke="${selectedRing}" stroke-width="1.5" opacity="0.9"/>
    `
    : '';

  const { delaySec, durSec } = markerPulseVarsFromSeed(pulseSeed ?? `${eventType}-beacon`);

  const svgMarkup = `
    <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <radialGradient id="${uid}-shell" cx="${cx - R * 0.22}" cy="${cyOrb - R * 0.38}" r="${R * 1.22}" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.58"/>
          <stop offset="46%" stop-color="${fillColor}"/>
          <stop offset="100%" stop-color="${fillColor}" stop-opacity="0.88"/>
        </radialGradient>
        <linearGradient id="${uid}-stem" x1="${cx - stemW / 2}" y1="${stemTop}" x2="${cx + stemW / 2}" y2="${stemTop}" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="rgba(120, 130, 140, 0.72)"/>
          <stop offset="32%" stop-color="rgba(245, 247, 250, 0.96)"/>
          <stop offset="58%" stop-color="rgba(180, 188, 198, 0.88)"/>
          <stop offset="100%" stop-color="rgba(82, 92, 104, 0.72)"/>
        </linearGradient>
        <linearGradient id="${uid}-cone" x1="${cx - coneHalfW}" y1="${coneTop}" x2="${cx + coneHalfW}" y2="${coneBottom}" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="rgba(226, 232, 240, 0.92)"/>
          <stop offset="42%" stop-color="rgba(255, 255, 255, 0.96)"/>
          <stop offset="72%" stop-color="rgba(148, 163, 184, 0.86)"/>
          <stop offset="100%" stop-color="rgba(71, 85, 105, 0.68)"/>
        </linearGradient>
        <filter id="${uid}-float" x="-55%" y="-55%" width="210%" height="210%">
          <feDropShadow dx="0" dy="5" stdDeviation="4" flood-color="#1c1917" flood-opacity="0.22"/>
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#ffffff" flood-opacity="0.35"/>
        </filter>
        <filter id="${uid}-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="3.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      <ellipse cx="${cx}" cy="${shadowCy}" rx="${shadowRx}" ry="${shadowRy}" fill="rgba(28,25,23,0.2)"/>

      <g filter="url(#${uid}-float)">
        <path d="M${cx - coneHalfW} ${coneBottom} L${cx - stemW / 2} ${coneTop} H${cx + stemW / 2} L${cx + coneHalfW} ${coneBottom} Z" fill="url(#${uid}-cone)" stroke="rgba(255,255,255,0.42)" stroke-width="0.6"/>
        <rect x="${cx - stemW / 2}" y="${stemTop}" width="${stemW}" height="${stemH}" rx="${stemW / 2}" fill="url(#${uid}-stem)" stroke="rgba(255,255,255,0.46)" stroke-width="0.45"/>
        <path d="M${cx - stemW * 0.18} ${stemTop + 1.2} V${coneTop - 1.2}" stroke="rgba(255,255,255,0.62)" stroke-width="0.75" stroke-linecap="round"/>
        ${selectedExtras}
        <circle cx="${cx}" cy="${cyOrb}" r="${R}" fill="url(#${uid}-shell)" stroke="${selectedRing}" stroke-width="${selectedRingW}"/>
        <circle cx="${cx}" cy="${cyOrb}" r="${innerR}" fill="#fafaf9" stroke="rgba(28,25,23,0.035)" stroke-width="0.75"/>
        <circle cx="${cx}" cy="${cyOrb}" r="${innerR - 0.5}" fill="none" stroke="${fillColor}" stroke-width="0.75" opacity="0.14"/>
      </g>

      <foreignObject x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}">
        <div xmlns="http://www.w3.org/1999/xhtml" style="width:${iconSize}px;height:${iconSize}px;display:flex;align-items:center;justify-content:center;">
          ${iconMarkup}
        </div>
      </foreignObject>

      ${freeBead}
    </svg>
  `;

  const wrapStyle = [
    'width:100%',
    'height:100%',
    'display:block',
    'line-height:0',
    `--hb-pulse-delay:${delaySec}s`,
    `--hb-pulse-dur:${durSec}s`,
  ].join(';');

  const html = `<div class="hb-marker-icon-wrap" style="${wrapStyle}"><div class="hb-marker-icon-inner">${svgMarkup}</div></div>`;

  return L.divIcon({
    className,
    html,
    iconSize: [W, H],
    iconAnchor: [cx, H],
    popupAnchor: [0, -H + 12],
  });
}
